import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkSlash } from '@fortawesome/pro-light-svg-icons/faLinkSlash';
import { TypedMemo } from '../../common/types';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { assetShareMessages } from './assetShareMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** `notFound`: an unknown or turned-off link (API 404). `gone`: a live link whose file was removed (API 410). */
export type AssetShareMissingKind = 'notFound' | 'gone';

/**
 * The page of a link that does not work, in the style of `PublicModeError`. One text for an
 * unknown token, a turned-off link and a deleted asset, so the page says nothing about the asset.
 * A visitor gets the trial button; a signed-in user goes to Home.
 */
const AssetShareMissingComponent: React.FC<{ kind: AssetShareMissingKind }> = ({ kind }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const userToken = useUserToken();
  const isAuthenticated = userToken !== null && userToken !== undefined;

  const handleAction = useCallback(() => {
    router.push(isAuthenticated ? SMLRoutes.home : SMLRoutes.auth);
  }, [isAuthenticated, router]);

  return (
    <SMLContainer publicMode header={null}>
      <div className={`${styles.missing} text-center mx-auto p-5`} data-testid="asset-share-missing">
        <FontAwesomeIcon icon={faLinkSlash} className={`${styles.glyph} mb-3`} />
        <h3 className="mb-3">
          {intl.formatMessage(kind === 'gone' ? assetShareMessages.goneTitle : assetShareMessages.notFoundTitle)}
        </h3>
        <p className="mb-4">
          {intl.formatMessage(kind === 'gone' ? assetShareMessages.goneText : assetShareMessages.notFoundText)}
        </p>
        <Button color="primary" onClick={handleAction}>
          {intl.formatMessage(isAuthenticated ? assetShareMessages.openHome : assetShareMessages.startTrial)}
        </Button>
      </div>
    </SMLContainer>
  );
};

export const AssetShareMissing = TypedMemo(AssetShareMissingComponent);
